<script setup lang="ts">
  import { DashboardOutlined } from '@ant-design/icons-vue'

  defineProps<{
    inFlightCount: number
    isActive: boolean
    isLoading: boolean
  }>()

  const emit = defineEmits<{
    (e: 'open'): void
  }>()

  function handleActivate() {
    emit('open')
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      emit('open')
    }
  }
</script>

<template>
  <div
    class="metrics-badge"
    :class="{ 'metrics-badge--active': isActive }"
    role="button"
    tabindex="0"
    :aria-label="`Developer metrics: ${inFlightCount} in flight`"
    @click="handleActivate"
    @keydown="handleKeydown"
  >
    <a-spin v-if="isLoading" size="small" />
    <template v-else>
      <DashboardOutlined class="metrics-badge__icon" />
      <span v-if="inFlightCount > 0" class="metrics-badge__count">{{ inFlightCount }}</span>
      <span v-if="isActive" class="metrics-badge__pulse" />
    </template>
  </div>
</template>

<style scoped>
.metrics-badge {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--color-background, #ffffff);
  border: 1px solid var(--color-border, #d9d9d9);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  z-index: 1000;
  transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  user-select: none;
}

.metrics-badge:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transform: scale(1.05);
}

.metrics-badge:focus-visible {
  outline: 2px solid #1677ff;
  outline-offset: 2px;
}

.metrics-badge--active {
  background-color: #e6f4ff;
  border-color: #1677ff;
}

.metrics-badge__icon {
  font-size: 20px;
  color: var(--color-text-secondary, #666666);
}

.metrics-badge--active .metrics-badge__icon {
  color: #1677ff;
}

.metrics-badge__count {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background-color: #1677ff;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.metrics-badge__pulse {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #1677ff;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.3);
  }
}
</style>
